import { useEffect, useState, useRef } from "react";
import * as Icons from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { toast } from "sonner";
import CostMeter, { estimateCost, formatUSD } from "./CostMeter";
import OutroAndMusicControls from "./OutroAndMusicControls";
import ReelOverlayControls from "./ReelOverlayControls";
import CinematicAndCaptionsControls from "./CinematicAndCaptionsControls";
import AspectExportControls from "./AspectExportControls";

export default function ReelComposer({ templates, listingId, defaultTitle, prefill, onPrefillConsumed, onReelCreated }) {
  const [shots, setShots] = useState([]);
  const [model, setModel] = useState("seedance-lite-720p");
  const [aspect, setAspect] = useState("widescreen_16_9");
  const [title, setTitle] = useState(defaultTitle || "");
  const [voEnabled, setVoEnabled] = useState(false);
  const [voText, setVoText] = useState("");
  const [voiceId, setVoiceId] = useState("21m00Tcm4Tlmv3GHvAln");
  const [submitting, setSubmitting] = useState(false);
  const [outroEnabled, setOutroEnabled] = useState(false);
  const [introCardEnabled, setIntroCardEnabled] = useState(false);
  const [presentedByEnabled, setPresentedByEnabled] = useState(false);
  const [transitionStyle, setTransitionStyle] = useState("crossfade");
  const [transitionDuration, setTransitionDuration] = useState(0.6);
  const [transitionOptions, setTransitionOptions] = useState([]);
  const [overlays, setOverlays] = useState({});
  const [musicTrackId, setMusicTrackId] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [speedRampOptions, setSpeedRampOptions] = useState([]);
  // New cinematic options — 3D address-text intro frame + burn-in captions
  const [cinematicEnabled, setCinematicEnabled] = useState(false);
  const [cinematicMaterial, setCinematicMaterial] = useState("gold");
  const [cinematicPlacement, setCinematicPlacement] = useState("sky");
  const [cinematicText, setCinematicText] = useState("");
  const [captionsEnabled, setCaptionsEnabled] = useState(false);
  const [captionsStyle, setCaptionsStyle] = useState("bold_white");
  const [logoIntroEnabled, setLogoIntroEnabled] = useState(false);
  const [logoIntroStyle, setLogoIntroStyle] = useState("glint_sweep");
  const [extraAspects, setExtraAspects] = useState([]);
  const fileRef = useRef();
  const previewAudio = useRef(null);

  useEffect(() => {
    api.get("/speed-ramps").then(({ data }) => setSpeedRampOptions(data || [])).catch(() => {});
  }, []);

  const loadRecipes = async () => {
    try {
      const { data } = await api.get("/recipes");
      setRecipes(data);
    } catch {
      // non-fatal — just no recipes dropdown
    }
  };
  useEffect(() => {
    loadRecipes();
  }, []);

  // Load transition catalogue (cached endpoint)
  useEffect(() => {
    api
      .get("/transitions")
      .then((r) => setTransitionOptions(r.data || []))
      .catch(() => setTransitionOptions([]));
  }, []);

  // Hydrate from a previous reel if user clicked "Use as starting point"
  useEffect(() => {
    if (!prefill) return;
    setTitle(`${prefill.title || "Listing reel"} — copy`);
    setModel(prefill.model || "seedance-lite-720p");
    setAspect(prefill.aspect_ratio || "widescreen_16_9");
    if (prefill.voiceover_text) {
      setVoEnabled(true);
      setVoText(prefill.voiceover_text);
      setVoiceId(prefill.voice_id || "21m00Tcm4Tlmv3GHvAln");
    }
    const newShots = (prefill.shots || []).map((s) => ({
      uid: crypto.randomUUID(),
      image_id: s.image_id,
      image_url: s.image_url ? mediaUrl(s.image_url) : null,
      template_id: s.template_id || null,
      template_name: s.template_name || null,
      prompt: s.user_prompt || s.prompt || "",
      duration: s.duration || "5",
      intensity: s.intensity || "subtle",
    }));
    setShots(newShots);
    onPrefillConsumed?.();
    toast.success("Loaded reel — tweak and re-run");
  }, [prefill, onPrefillConsumed]);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || e.dataTransfer?.files || []);
    if (files.length === 0) return;
    const total = files.length;
    const toastId = toast.loading(`Uploading 0/${total}…`);
    let done = 0;
    const results = await Promise.allSettled(
      files.map(async (f) => {
        const fd = new FormData();
        fd.append("file", f);
        if (listingId) fd.append("listing_id", listingId);
        const { data } = await api.post("/images", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        done += 1;
        toast.loading(`Uploading ${done}/${total}…`, { id: toastId });
        return data;
      })
    );
    const ok = results.filter((r) => r.status === "fulfilled");
    const failed = results.length - ok.length;
    const newShots = ok.map((r) => ({
      uid: crypto.randomUUID(),
      image_id: r.value.id,
      image_url: mediaUrl(r.value.url),
      template_id: templates[0]?.id || null,
      template_name: templates[0]?.name || null,
      prompt: templates[0]?.prompt || "",
      duration: "5",
      intensity: "subtle",
    }));
    setShots((s) => [...s, ...newShots]);
    if (failed > 0) {
      toast.error(`${ok.length}/${total} uploaded, ${failed} failed`, { id: toastId });
    } else {
      toast.success(`${ok.length} photo${ok.length === 1 ? "" : "s"} uploaded`, { id: toastId });
    }
    if (e.target) e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer?.files?.length) {
      onFiles({ target: { files: e.dataTransfer.files } });
    }
  };
  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const updateShot = (uid, patch) =>
    setShots((s) => s.map((x) => (x.uid === uid ? { ...x, ...patch } : x)));

  const removeShot = (uid) =>
    setShots((s) => s.filter((x) => x.uid !== uid));

  const moveShot = (uid, dir) => {
    setShots((s) => {
      const i = s.findIndex((x) => x.uid === uid);
      if (i < 0) return s;
      const j = i + dir;
      if (j < 0 || j >= s.length) return s;
      const next = [...s];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const setShotTemplate = (uid, tplId) => {
    const t = templates.find((x) => x.id === tplId);
    if (!t) return;
    updateShot(uid, {
      template_id: t.id,
      template_name: t.name,
      prompt: t.prompt,
    });
  };

  const applyRecipe = (uid, recipeId) => {
    if (!recipeId) return;
    const r = recipes.find((x) => x.id === recipeId);
    if (!r) return;
    const t = templates.find((x) => x.id === r.template_id);
    const basePrompt = t?.prompt || "";
    const suffix = r.prompt_suffix || "";
    updateShot(uid, {
      template_id: r.template_id,
      template_name: t?.name || r.template_id,
      prompt: basePrompt + suffix,
      duration: r.duration || "5",
      intensity: r.intensity || "subtle",
      _recipe_id: r.id,
    });
    toast.success(`Applied "${r.name}"`);
  };

  const saveAsRecipe = async (s) => {
    const name = window.prompt(
      "Name this recipe (e.g. 'Front of house — my style'):",
      s.template_name ? `${s.template_name} — my style` : "My recipe"
    );
    if (!name || !name.trim()) return;
    const label = window.prompt(
      "Photo type this is best for (e.g. Front of House, Kitchen, Bedroom)? Optional:",
      ""
    );
    // Separate the template's default prompt from whatever suffix the user added
    const t = templates.find((x) => x.id === s.template_id);
    const basePrompt = t?.prompt || "";
    const prompt_suffix =
      s.prompt && s.prompt.startsWith(basePrompt)
        ? s.prompt.slice(basePrompt.length)
        : s.prompt || "";
    try {
      await api.post("/recipes", {
        name: name.trim(),
        label: (label || "").trim() || null,
        template_id: s.template_id,
        prompt_suffix,
        duration: s.duration,
        intensity: s.intensity,
        aspect_ratio: aspect,
        camera_fixed: false,
        model,
      });
      await loadRecipes();
      toast.success("Recipe saved");
    } catch {
      toast.error("Could not save recipe");
    }
  };

  const aiSuggestShot = async (uid, image_id) => {
    updateShot(uid, { _analyzing: true });
    try {
      const { data } = await api.post(`/images/${image_id}/analyze`);
      const top = data.recommendations?.[0];
      if (!top) {
        toast.error("AI returned no recommendation");
        return;
      }
      const t = templates.find((x) => x.id === top.template_id);
      updateShot(uid, {
        _analyzing: false,
        template_id: top.template_id,
        template_name: t?.name || top.template_id,
        prompt: top.custom_prompt || t?.prompt || "",
      });
      toast.success(`AI picked: ${t?.name || top.template_id}`);
    } catch (e) {
      updateShot(uid, { _analyzing: false });
      toast.error("AI analyze failed");
    }
  };

  const previewVoice = async () => {
    if (!voText.trim()) return;
    try {
      const { data } = await api.post(
        "/voiceover/preview",
        { text: voText.trim(), voice_id: voiceId },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(data);
      if (previewAudio.current) {
        previewAudio.current.pause();
      }
      const a = new Audio(url);
      previewAudio.current = a;
      a.play();
    } catch (e) {
      toast.error("Voice preview failed");
    }
  };

  const generate = async () => {
    if (shots.length < 1) {
      toast.error("Add at least one image");
      return;
    }
    if (shots.some((s) => !s.prompt.trim())) {
      toast.error("All shots need a prompt");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/reels", {
        title: title.trim() || defaultTitle || "Untitled Listing Reel",
        model,
        aspect_ratio: aspect,
        voiceover_text: voEnabled && voText.trim() ? voText.trim() : null,
        voice_id: voiceId,
        listing_id: listingId || null,
        outro_enabled: outroEnabled,
        intro_card_enabled: introCardEnabled,
        presented_by_enabled: presentedByEnabled,
        transition_style: transitionStyle,
        transition_duration: transitionDuration,
        overlays,
        music_track_id: musicTrackId || null,
        cinematic_text_enabled: cinematicEnabled,
        cinematic_text_opts: cinematicEnabled
          ? {
              material: cinematicMaterial,
              placement: cinematicPlacement,
              duration: 3.0,
              text: cinematicText.trim() || undefined,
            }
          : null,
        captions_enabled: captionsEnabled && voEnabled && voText.trim().length > 0,
        captions_style: captionsStyle,
        logo_intro_enabled: logoIntroEnabled,
        logo_intro_style: logoIntroStyle,
        extra_aspects: extraAspects,
        shots: shots.map((s) => ({
          image_id: s.image_id,
          prompt: s.prompt,
          template_id: s.template_id,
          template_name: s.template_name,
          duration: s.duration,
          intensity: s.intensity || "subtle",
          speed_ramp: s.speed_ramp || "linear",
        })),
      });
      toast.success("Reel generation started");
      onReelCreated?.(data);
      setShots([]);
      setTitle("");
      setVoText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create reel");
    } finally {
      setSubmitting(false);
    }
  };

  const totalSeconds = shots.reduce(
    (acc, s) => acc + parseInt(s.duration || "5"),
    0
  );
  const totalCost = shots.reduce(
    (acc, s) =>
      acc + estimateCost({ model, durationSec: parseInt(s.duration || "5") }),
    0
  );

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4 flex flex-wrap items-center gap-3">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Listing reel title (e.g. 1247 Lakeshore Drive)"
          className="flex-1 min-w-[240px] bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30"
          data-testid="reel-title-input"
        />
        <select
          value={model}
          onChange={(e) => setModel(e.target.value)}
          className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
          data-testid="reel-model-select"
        >
          <option value="seedance-pro-1080p">Pro · 1080p</option>
          <option value="seedance-lite-720p">Lite · 720p</option>
          <option value="seedance-lite-480p">Lite · 480p</option>
        </select>
        <select
          value={aspect}
          onChange={(e) => setAspect(e.target.value)}
          className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
          data-testid="reel-aspect-select"
        >
          <option value="widescreen_16_9">16:9</option>
          <option value="vertical_9_16">9:16</option>
          <option value="square_1_1">1:1</option>
        </select>
        <div className="text-xs text-zinc-500 font-mono">
          {shots.length} shots · ~{totalSeconds}s
        </div>
        <div
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-500/10 border border-amber-500/20 text-amber-300"
          title="Estimated Magnific cost based on Seedance pricing"
          data-testid="reel-cost-meter"
        >
          <Icons.Coins size={11} />
          <span className="text-[10px] font-mono uppercase tracking-wider">
            {formatUSD(totalCost)}
          </span>
        </div>
        <button
          onClick={generate}
          disabled={submitting || shots.length === 0}
          className="bg-white text-black font-medium rounded-lg px-5 py-2 hover:bg-zinc-200 active:scale-[0.98] disabled:opacity-30 transition-all flex items-center gap-2"
          data-testid="reel-generate-button"
        >
          {submitting ? (
            <Icons.Loader2 className="animate-spin" size={14} />
          ) : (
            <Icons.Film size={14} />
          )}
          Generate Reel
        </button>
      </div>

      {/* Outro + Music */}
      <OutroAndMusicControls
        outroEnabled={outroEnabled}
        onOutroChange={setOutroEnabled}
        musicTrackId={musicTrackId}
        onMusicChange={setMusicTrackId}
      />

      {/* NEW cinematic options — 3D address text + burn-in captions */}
      <CinematicAndCaptionsControls
        cinematicEnabled={cinematicEnabled}
        setCinematicEnabled={setCinematicEnabled}
        cinematicMaterial={cinematicMaterial}
        setCinematicMaterial={setCinematicMaterial}
        cinematicPlacement={cinematicPlacement}
        setCinematicPlacement={setCinematicPlacement}
        cinematicText={cinematicText}
        setCinematicText={setCinematicText}
        captionsEnabled={captionsEnabled}
        setCaptionsEnabled={setCaptionsEnabled}
        captionsStyle={captionsStyle}
        setCaptionsStyle={setCaptionsStyle}
        logoIntroEnabled={logoIntroEnabled}
        setLogoIntroEnabled={setLogoIntroEnabled}
        logoIntroStyle={logoIntroStyle}
        setLogoIntroStyle={setLogoIntroStyle}
        voiceoverEnabled={voEnabled && voText.trim().length > 0}
        listingId={listingId}
      />

      <AspectExportControls
        extraAspects={extraAspects}
        setExtraAspects={setExtraAspects}
      />

      {/* Intro card toggle */}
      <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={introCardEnabled}
            onChange={(e) => setIntroCardEnabled(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded bg-white/5 border border-white/20 checked:bg-white accent-white"
            data-testid="intro-card-toggle"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-0.5">
              <Icons.Tag size={12} className="text-amber-400" />
              <div className="text-[11px] tracking-[0.15em] uppercase text-white">
                Prepend listing card
              </div>
            </div>
            <div className="text-[11px] text-zinc-500 leading-relaxed">
              3-second title card at the start of the reel — price, address, and
              property stats in your brand color. Edit stats via the price tag
              icon on the listing in the sidebar.
            </div>
          </div>
        </label>
      </div>

      {/* Presented-by toggle */}
      <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={presentedByEnabled}
            onChange={(e) => setPresentedByEnabled(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded bg-white/5 border border-white/20 checked:bg-white accent-white"
            data-testid="presented-by-toggle"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-0.5">
              <Icons.UserCircle2 size={12} className="text-sky-400" />
              <div className="text-[11px] tracking-[0.15em] uppercase text-white">
                Prepend "Presented by" card
              </div>
            </div>
            <div className="text-[11px] text-zinc-500 leading-relaxed">
              4-second intro slide: address + realtor headshot + name +
              brokerage + phone + email. Plays before the reel starts.
            </div>
          </div>
        </label>
      </div>

      {/* Transition picker */}
      <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Icons.Wand2 size={12} className="text-violet-400" />
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
            Transition between clips
          </div>
        </div>
        {transitionOptions.length === 0 ? (
          <div className="text-[11px] text-zinc-600">Loading…</div>
        ) : (
          <>
            <div className="flex flex-wrap gap-1.5">
              {transitionOptions.map((t) => {
                const on = transitionStyle === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => {
                      setTransitionStyle(t.id);
                      if (t.default_duration && t.default_duration > 0) {
                        setTransitionDuration(t.default_duration);
                      } else {
                        setTransitionDuration(0);
                      }
                    }}
                    className={`text-xs rounded-lg px-3 py-1.5 border transition-all ${
                      on
                        ? "bg-white text-black border-white"
                        : "bg-black/40 border-white/10 hover:border-white/30 text-white"
                    }`}
                    title={t.blurb}
                    data-testid={`transition-${t.id}`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
            {transitionStyle !== "cut" && (
              <div className="mt-3 flex items-center gap-3">
                <span className="text-[11px] text-zinc-500">Duration</span>
                <input
                  type="range"
                  min="0.2"
                  max="1.5"
                  step="0.1"
                  value={transitionDuration}
                  onChange={(e) => setTransitionDuration(Number(e.target.value))}
                  className="flex-1 max-w-[260px] accent-white"
                  data-testid="transition-duration"
                />
                <span className="text-[11px] font-mono text-zinc-400 w-10 text-right">
                  {transitionDuration.toFixed(1)}s
                </span>
              </div>
            )}
            <div className="text-[11px] text-zinc-600 mt-2 leading-relaxed">
              {(transitionOptions.find((t) => t.id === transitionStyle) || {}).blurb}
            </div>
          </>
        )}
      </div>

      {/* Persistent overlays */}
      <ReelOverlayControls
        listingId={listingId}
        value={overlays}
        onChange={setOverlays}
      />
      {/* Voice-over panel */}
      <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <Icons.Mic size={14} className="text-zinc-500" />
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
              Voice Over (optional)
            </div>
          </div>
          <button
            onClick={() => setVoEnabled((v) => !v)}
            className={`relative w-10 h-5 rounded-full transition-all ${
              voEnabled ? "bg-white" : "bg-white/10"
            }`}
            data-testid="reel-vo-toggle"
          >
            <div
              className={`absolute top-0.5 w-4 h-4 rounded-full transition-all ${
                voEnabled
                  ? "translate-x-5 bg-black"
                  : "translate-x-0.5 bg-white/60"
              }`}
            />
          </button>
        </div>
        {voEnabled && (
          <div className="space-y-3">
            <textarea
              value={voText}
              onChange={(e) => setVoText(e.target.value)}
              rows={3}
              placeholder="Welcome to 1247 Lakeshore Drive — a five-bedroom waterfront retreat with sweeping views of the bay…"
              className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30"
              data-testid="reel-vo-text"
            />
            <div className="flex items-center gap-3">
              <select
                value={voiceId}
                onChange={(e) => setVoiceId(e.target.value)}
                className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                data-testid="reel-vo-voice"
              >
                <option value="21m00Tcm4Tlmv3GHvAln">Rachel</option>
                <option value="pNInz6obpgDQGcFmaJgB">Adam</option>
                <option value="EXAVITQu4vr4xnSDxMaL">Bella</option>
                <option value="ErXwobaYiN019PkySvjV">Antoni</option>
                <option value="MF3mGyEYCl7XYWbV9V6O">Elli</option>
              </select>
              <button
                onClick={previewVoice}
                disabled={!voText.trim()}
                className="text-xs text-white border border-white/20 rounded-lg px-3 py-2 hover:bg-white/10 disabled:opacity-30 flex items-center gap-1.5"
                data-testid="reel-vo-preview"
              >
                <Icons.Play size={12} /> Preview
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Timeline */}
      <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
            Timeline
          </div>
          <button
            onClick={() => fileRef.current?.click()}
            className="text-xs text-white border border-white/20 rounded-lg px-3 py-1.5 hover:bg-white/10 flex items-center gap-1.5"
            data-testid="reel-add-images"
          >
            <Icons.Plus size={12} /> Add images
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            hidden
            onChange={onFiles}
          />
        </div>

        {shots.length === 0 ? (
          <div
            className="border border-dashed border-white/10 rounded-lg p-12 text-center hover:border-white/30 transition-all cursor-pointer"
            onClick={() => fileRef.current?.click()}
            onDrop={onDrop}
            onDragOver={onDragOver}
            data-testid="reel-empty-dropzone"
          >
            <Icons.Layers className="mx-auto text-zinc-700" size={28} />
            <div className="text-sm text-zinc-500 mt-3">
              Add house photos to compose a multi-shot listing reel.
            </div>
            <div className="text-[11px] text-zinc-600 mt-1">
              Click to browse or drag-and-drop multiple files
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto scrollbar-hide">
            <div className="flex gap-3 pb-2">
              {shots.map((s, i) => (
                <div
                  key={s.uid}
                  className="w-[260px] shrink-0 bg-black/40 border border-white/10 rounded-lg overflow-hidden"
                  data-testid={`reel-shot-${i}`}
                >
                  <div className="relative aspect-video bg-black">
                    <img
                      src={s.image_url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 left-2 px-1.5 py-0.5 text-[10px] font-mono bg-black/80 rounded text-white">
                      #{i + 1}
                    </div>
                    <div className="absolute top-2 right-2 flex gap-1">
                      <button
                        onClick={() => aiSuggestShot(s.uid, s.image_id)}
                        disabled={s._analyzing}
                        className="p-1 bg-black/80 rounded hover:bg-white hover:text-black disabled:opacity-50"
                        title="AI suggest motion"
                        data-testid={`reel-shot-${i}-ai`}
                      >
                        {s._analyzing ? (
                          <Icons.Loader2 size={12} className="animate-spin" />
                        ) : (
                          <Icons.Sparkles size={12} />
                        )}
                      </button>
                      <button
                        onClick={() => moveShot(s.uid, -1)}
                        className="p-1 bg-black/80 rounded hover:bg-white hover:text-black"
                      >
                        <Icons.ChevronLeft size={12} />
                      </button>
                      <button
                        onClick={() => moveShot(s.uid, 1)}
                        className="p-1 bg-black/80 rounded hover:bg-white hover:text-black"
                      >
                        <Icons.ChevronRight size={12} />
                      </button>
                      <button
                        onClick={() => removeShot(s.uid)}
                        className="p-1 bg-black/80 rounded hover:bg-red-500 hover:text-white"
                      >
                        <Icons.X size={12} />
                      </button>
                    </div>
                  </div>
                  <div className="p-2.5 space-y-2">
                    {recipes.length > 0 && (
                      <div className="flex gap-1">
                        <select
                          value={s._recipe_id || ""}
                          onChange={(e) => applyRecipe(s.uid, e.target.value)}
                          className="flex-1 min-w-0 bg-emerald-500/5 border border-emerald-500/20 rounded px-2 py-1.5 text-xs text-emerald-300 truncate"
                          data-testid={`reel-shot-${i}-recipe`}
                          title="Apply a saved recipe to this shot"
                        >
                          <option value="">✨ Recipe…</option>
                          {recipes.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.label ? `${r.label.slice(0, 10)} · ` : ""}
                              {r.name.length > 22 ? r.name.slice(0, 22) + "…" : r.name}
                              {!r.is_builtin ? " ★" : ""}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => saveAsRecipe(s)}
                          className="shrink-0 px-2 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded text-[10px] text-emerald-300 hover:bg-emerald-500/20 flex items-center gap-1"
                          title="Save current settings as a reusable recipe"
                          data-testid={`reel-shot-${i}-save-recipe`}
                        >
                          <Icons.BookmarkPlus size={11} />
                        </button>
                      </div>
                    )}
                    <select
                      value={s.template_id || ""}
                      onChange={(e) => setShotTemplate(s.uid, e.target.value)}
                      className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs text-white"
                      data-testid={`reel-shot-${i}-template`}
                    >
                      {templates.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                    <select
                      value={s.duration}
                      onChange={(e) =>
                        updateShot(s.uid, { duration: e.target.value })
                      }
                      className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs text-white"
                    >
                      <option value="3">3s</option>
                      <option value="5">5s</option>
                      <option value="7">7s</option>
                      <option value="10">10s</option>
                    </select>
                    {speedRampOptions.length > 0 && (
                      <select
                        value={s.speed_ramp || "linear"}
                        onChange={(e) =>
                          updateShot(s.uid, { speed_ramp: e.target.value })
                        }
                        className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs text-white"
                        title="Variable-speed playback (cinematic ramp)"
                        data-testid={`reel-shot-${i}-speed-ramp`}
                      >
                        {speedRampOptions.map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.id === "linear" ? "Speed: 1×" : `Ramp: ${o.label.replace(/^.*?: /, "")}`}
                          </option>
                        ))}
                      </select>
                    )}
                    <select
                      value={s.intensity || "subtle"}
                      onChange={(e) =>
                        updateShot(s.uid, { intensity: e.target.value })
                      }
                      className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs text-white"
                      data-testid={`reel-shot-${i}-intensity`}
                    >
                      <option value="whisper">Whisper · locked-off</option>
                      <option value="subtle">Subtle · barely moves</option>
                      <option value="standard">Standard · gentle</option>
                      <option value="cinematic">Cinematic · full motion</option>
                    </select>
                    <textarea
                      value={s.prompt}
                      onChange={(e) =>
                        updateShot(s.uid, { prompt: e.target.value })
                      }
                      rows={3}
                      className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-[11px] text-white font-mono leading-snug resize-none"
                      data-testid={`reel-shot-${i}-prompt`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
